import type { ReactNode } from "react";
import type { BadgeVariants } from "@ubnt/store.core/lib/components/Badge/Badge.types";
import type {
  SiteId,
  SiteUrl,
} from "@ubnt/store.core/lib/contexts/MenuContext/mapping";
import type { StoreId } from "../../types";
import type {
  CategorySlug,
  PrimaryCategoryId,
} from "@ubnt/store.core/lib/constants/proStoreCategories/mapping/mapping";
import type { en as EN_TRANSLATION } from "./translations";

export interface StaticImageData {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
}

export type TranslationKey = keyof typeof EN_TRANSLATION;

export enum ProductId {
  AiMs4 = "aiMs4",
  DoorHub = "doorHub",
  DoorHubMini = "doorHubMini",
  G2ReaderPro = "g2ReaderPro",
  G2Reader = "g2Reader",
  G3Reader = "g3Reader",
  G3TouchWall = "g3TouchWall",
  G3TouchEnterprise = "g3TouchEnterprise",
  G3TouchPro = "g3TouchPro",
  G3ReaderPro = "g3ReaderPro",
  G3ReaderFlex = "g3ReaderFlex",
  G3StarterKit = "g3StarterKit",
  G3StarterKitPro = "g3StarterKitPro",
  ReaderLite = "readerLite",
  _8PoEGen1 = "_8PoEGen1",
  _8PoESFPGen1 = "_8PoESFPGen1",
  AccessHub = "accessHub",
  UaUltra = "uaUltra",
  AcInWall = "acInWall",
  AcMesh = "acMesh",
  AcMeshProfessional = "acMeshProfessional",
  Aggregation = "aggregation",
  Ai360 = "ai360",
  AiBullet = "aiBullet",
  AiDSLR = "aiDSLR",
  AiPro = "aiPro",
  AiTurret = "aiTurret",
  AiDome = "aiDome",
  AiLpr = "aiLpr",
  BuildingBridge = "buildingBridge",
  BuildingBridgeXG = "buildingBridgeXG",
  BaseStationXG = "baseStationXG",
  ConnectDisplay = "connectDisplay",
  CloudKeyEnterprise = "cloudKeyEnterprise",
  CloudKeyG2Plus = "cloudKeyG2Plus",
  ElevatorHub = "elevatorHub",
  Efg = "efg",
  Enterprise8PoE = "enterprise8PoE",
  Enterprise24PoE = "enterprise24PoE",
  Enterprise48PoE = "enterprise48PoE",
  EnterpriseAccessHub = "enterpriseAccessHub",
  EnterpriseXG24 = "enterpriseXG24",
  E7 = "e7",
  E7Campus = "e7Campus",
  E7Audience = "e7Audience",
  Fiber = "fiber",
  EvStationPro = "evStationPro",
  EvStationLite = "evStationLite",
  Flex = "flex",
  Flex2_5G = "flex2_5G",
  Flex2_5GPoE = "flex2_5GPoE",
  Flex10GbE = "flex10GbE",
  FlexMini = "flexMini",
  FlexMini2_5G = "flexMini2_5G",
  GatewayLite = "gatewayLite",
  GatewayProfessional = "gatewayProfessional",
  GatewayEnterprise = "gatewayEnterprise",
  GatewayMax = "gatewayMax",
  G4Instant = "g4Instant",
  Gpon = "gpon",
  G4PTZ = "g4PTZ",
  G5PTZ = "g5PTZ",
  G5Bullet = "g5Bullet",
  G5Dome = "g5Dome",
  G5Flex = "g5Flex",
  G5Pro = "g5Pro",
  G5DomeUltra = "g5DomeUltra",
  G5TurretUltra = "g5TurretUltra",
  HiCapacityAggregation = "hiCapacityAggregation",
  Industrial = "industrial",
  Intercom = "intercom",
  Lite8POE = "lite8PoE",
  Lite16POE = "lite16PoE",
  LteBackup = "lteBackup",
  LteBackupProfessional = "lteBackupProfessional",
  MissionCritical = "missionCritical",
  Nvr = "nvr",
  NvrPro = "nvrPro",
  NvrInstant = "nvrInstant",
  Envr = "envr",
  OfficialUniFiHosting = "officialUniFiHosting",
  PhoneTouch = "phoneTouch",
  PhoneTouchMax = "phoneTouchMax",
  Professional8PoE = "professional8PoE",
  Professional24 = "professional24",
  Professional24PoE = "professional24PoE",
  Professional48 = "professional48",
  Professional48PoE = "professional48PoE",
  ProfessionalMax16 = "professionalMax16",
  ProfessionalMax24 = "professionalMax24",
  ProfessionalMax48 = "professionalMax48",
  ProfessionalMax16PoE = "professionalMax16PoE",
  ProfessionalMax24PoE = "professionalMax24PoE",
  ProfessionalMax48PoE = "professionalMax48PoE",
  ProLongDistance = "proLongDistance",
  Standard16PoE = "standard16PoE",
  Standard16PoEGen1 = "standard16PoEGen1",
  Standard24 = "standard24",
  Standard24PE = "standard24PoE",
  Standard24PoEGen1 = "standard24PoEGen1",
  Standard48 = "standard48",
  Standard48PoE = "standard48PoE",
  Standard48PoEGen1 = "standard48PoEGen1",
  SwitchesEnterprise = "switchesEnterprise",
  SwitchesProfessional = "switchesProfessional",
  SwitchesProfessionalMax = "switchesProfessionalMax",
  SwitchesStandard = "switchesStandard",
  SwitchesUtility = "switchesUtility",
  Theta = "theta",
  Theta360 = "theta360",
  ThetaAILongDistance = "thetaAILongDistance",
  ThetaPro = "thetaPro",
  ThetaPro360 = "thetaPro360",
  ThetaProWideAngle = "thetaProWideAngle",
  ThetaWideAngle = "thetaWideAngle",
  UispSwitching = "uispSwitching",
  U6Enterprise = "u6Enterprise",
  U6EnterpriseInWall = "u6EnterpriseInWall",
  U6Extender = "u6Extender",
  U6InWall = "u6InWall",
  U6Lite = "u6Lite",
  U6LR = "u6LR",
  U6Mesh = "u6Mesh",
  U6MeshPro = "u6MeshPro",
  U6Plus = "u6Plus",
  U6Pro = "u6Pro",
  U7Pro = "u7pro",
  U7ProOutdoor = "u7proOutdoor",
  U7ProWall = "u7proWall",
  U7ProXgWall = "u7proXgWall",
  UcgMax = "ucgMax",
  UcgUltra = "ucgUltra",
  UcCastLite = "ucCastLite",
  UcCastPro = "ucCastPro",
  UdmPro = "udmPro",
  UdmProMax = "udmProMax",
  UdmProSpecial = "udmProSpecial",
  Umr = "umr",
  UmrIndustrial = "umrIndustrial",
  UmrUltra = "umrUltra",
  U5GMax = "u5gMax",
  U5GMaxOutdoor = "u5gMaxOutdoor",
  Xg6PoEGen1 = "xg6PoEGen1",
  SwissArmyKnifeUltra = "swissArmyKnifeUltra",
  DreamWall = "udw",
  DreamRouter = "udr",
  Express = "express",
  SwitchUltra = "switchUltra",
  G2StarterKitPro = "g2StarterKitPro",
  G2StarterKit = "g2StarterKit",
  ElevatorStarterKit = "elevatorStarterKit",
  FiberWifi6 = "fiberWifi6",
  FiberWifi = "fiberWifi",
  FiberInstant = "fiberInstant",
  EdgeRouterX = "edgeRouterX",
  EdgeRouterXSfp = "edgeRouterXSfp",
  EdgeRouter6Poe = "edgeRouter6Poe",
  EdgeRouterInfinity = "edgeRouterInfinity",
  EdgeRouter4 = "edgeRouter4",
  EdgeRouter12 = "edgeRouter12",
  EdgeRouter12p = "edgeRouter12p",
  EdgeRouter10x = "edgeRouter10x",
  EdgePointS16 = "edgePointS16",
  EdgePointR8 = "edgePointR8",
  EdgePointR6 = "edgePointR6",
  Power = "Power",
  LtuLite = "ltuLite",
  LtuPro = "ltuPro",
  LtuLongRange = "ltuLongRange",
  LtuExtremeRange = "ltuExtremeRange",
  LtuInstant = "ltuInstant",
  PrismStation5AC = "prismStation5AC",
  RocketPrism5AC = "rocketPrism5AC",
  LiteAPGPS = "liteAPGPS",
  RocketACLite = "rocketACLite",
  NanoStation5AC = "nanoStation5AC",
  NanoBeam5AC = "nanoBeam5AC",
  NanoStation5ACLoco = "nanoStation5ACLoco",
  LiteBeam5ACExtremeRange = "liteBeam5ACExtremeRange",
  LiteBeam5ACLongRange = "liteBeam5ACLongRange",
  LiteBeam5AC = "liteBeam5AC",
  LiteBeamM5 = "liteBeamM5",
  PowerBeam5AC620 = "powerBeam5AC620",
  PowerBeam5ACISO = "powerBeam5ACISO",
  PowerBeam5AC = "powerBeam5AC",
  NanoBeam2AC = "nanoBeam2AC",
  PowerBeam2AC400 = "powerBeam2AC400",
  AirFiber2X = "airFiber2X",
  AirFiber5XHD = "airFiber5XHD",
  AirFiber5 = "airFiber5",
  AirFiber11 = "airFiber11",
  AirFiber24HiDensity = "airFiber24HiDensity",
  AirFiber24 = "airFiber24",
  UispHorn = "uispHorn",
  UispDish = "uispDish",
  AirFiberX23dBiSlant45 = "airFiberX23dBiSlant45",
  AirFiberX30dBiSlant45 = "airFiberX30dBiSlant45",
  AirFiberX34dBiSlant45 = "airFiberX34dBiSlant45",
  AirMAXAC30dBiRocketDish = "airMAXAC30dBiRocketDish",
  AirMAXAC34dBiRocketDish = "airMAXAC34dBiRocketDish",
  AirMAX30dBiRocketDishLW = "airMAX30dBiRocketDishLW",
  AirMAXAC31dBiRocketDish = "airMAXAC31dBiRocketDish",
  AirFiber37dBiMonsterDish = "airFiber37dBiMonsterDish",
  AirFiberX24dBiSlant45 = "airFiberX24dBiSlant45",
  AirMAX24GHz13dBiOmni = "airMAX24GHz13dBiOmni",
  AirMAX24GHz10dBiOmni = "airMAX24GHz10dBiOmni",
  AirMAX5GHz13dBiOmni = "airMAX5GHz13dBiOmni",
  AirMAX5GHz10dBiOmni = "airMAX5GHz10dBiOmni",
  AirPRISM3x30HDSector = "airPRISM3x30HDSector",
  AirMAXSector24GHzTitanium = "airMAXSector24GHzTitanium",
  AirMAX900MHz13dBi120Sector = "airMAX900MHz13dBi120Sector",
  AirMAXAC5GHz21dBi60Sector = "airMAXAC5GHz21dBi60Sector",
  AirMAXAC5GHz22dBi45Sector = "airMAXAC5GHz22dBi45Sector",
  // AirMAXSector5GHzTitanium = "airMAXSector5GHzTitanium",
  AirMAX24GHZ15dBi120Sector = "airMAX24GHZ15dBi120Sector",
  AirMAX24GHz16dBi90Sector = "airMAX24GHz16dBi90Sector",
  AirMAX5GHz19dBi120Sector = "airMAX5GHz19dBi120Sector",
  AirMAX5GHz20dBi90Sector = "airMAX5GHz20dBi90Sector",
  AirMAX5GHz16dBi120Sector = "airMAX5GHz16dBi120Sector",
  AirMAX5GHz17dBi90Sector = "airMAX5GHz17dBi90Sector",
  AirFiber = "airfiber",
  U7ProMax = "u7proMax",
  U7Outdoor = "u7outdoor",
  EdgeMaxSwitch = "edgeMaxSwitch",
  EdgeSwitch48poe = "edgeSwitch48poe",
  EdgeSwitch48poe750 = "edgeSwitch48poe750",
  EdgeSwitch24poe = "edgeSwitch24poe",
  EdgeSwitch8poe = "edgeSwitch8poe",
  EdgeSwitch5xp = "edgeSwitch5xp",
  EdgeSwitch16xg = "edgeSwitch16xg",
  EdgeSwitch24poe500 = "edgeSwitch24poe500",
  EdgeSwitch48lite = "edgeSwitch48lite",
  EdgeSwitch10x = "edgeSwitch10x",
  EdgeSwitch10xp = "edgeSwitch10xp",
  EdgeSwitch16poe150 = "edgeSwitch16poe150",
  EdgeSwitch16xp = "edgeSwitch16xp",
  EdgeSwitch18x = "edgeSwitch18x",
  EdgeSwitch24lite = "edgeSwitch24lite",
  EdgeSwitch12f = "edgeSwitch12f",
  GateAccessStarterKit = "gateAccessStarterKit",
  UdbPro = "udbPro",
  Udb = "udb",
  UdbIot = "udbIot",
  UdbSwitch = "udbSwitch",
  GateHub = "gateHub",
  WaveApGen2 = "waveApGen2",
  WaveFiberOlt = "waveFiberOlt",
  FiberOltXgs = "fiberOltXgs",
  WaveFiberOnu = "waveFiberOnu",
  WaveMLO5 = "waveMLO5",
  WaveMLO6 = "waveMLO6",
  EnterpriseCampusAggregation = "enterpriseCampusAggregation",
  EnterpriseCampus24 = "enterpriseCampus24",
  EnterpriseCampus24PoE = "enterpriseCampus24PoE",
  EnterpriseCampus48PoE = "enterpriseCampus48PoE",
  EnterpriseCampus48 = "enterpriseCampus48",
  ProfessionalHd24 = "professionalHd24",
  ProfessionalHd24Poe = "professionalHd24Poe",
  SwitchesProfessionalHd = "switchesProfessionalHd",
  SwitchesProfessionalXg = "switchesProfessionalXg",
  Express7 = "express7",
  UcgFiber = "ucgFiber",
  Udr7 = "udr7",
  Udr5gMax = "udr5gMax",
  GatewayFiber = "gatewayFiber",
  U7InWall = "u7InWall",
  U7Lite = "u7lite",
  ProXg8PoE = "proXg8PoE",
  ProfessionalXg48PoE = "professionalXg48PoE",
  // ProfessionalXg48 = "professionalXg48",
  ProfessionalXg24PoE = "professionalXg24PoE",
  // ProfessionalXg24 = "professionalXg24",
  ProfessionalXg10PoE = "professionalXg10PoE",
  U7proXg = "u7proXg",
  U7proXgs = "u7proXgs",
  G6Instant = "g6Instant",
  G6Turret = "g6Turret",
  G6Bullet = "g6Bullet",
  G6ProBullet = "g6ProBullet",
  WanSwitch = "wanSwitch",
  WanSwitchRj45 = "wanSwitchRj45",
  UdbProSector = "udbProSector",
  ProXgAggregation = "proXgAggregation",
  SwitchesWan = "switchesWan",
  G6PTZ = "g6Ptz",
  AiPtzIndustrial = "aiPtzIndustrial",
  AiPtzPrecision = "aiPtzPrecision",
  U7LR = "u7LR",
  G3Intercom = "g3Intercom",
  G6Dome = "g6Dome",
  G6ProDome = "g6ProDome",
  G6ProTurret = "g6ProTurret",
  G6180 = "g6180",
  G6Pro360 = "g6Pro360",
  Unas2 = "unas2",
  Unas4 = "unas4",
  UnasPro4 = "unasPro4",
  UnasPro8 = "unasPro8",
  UnasPro = "unasPro",
  UPSTower = "upsTower",
  UPS2U = "ups2u",
  RetrofitHub = "retrofitHub",
  RetrofitReader = "retrofitReader",
}

type EmptyColumnId = "emptyColumn";

export enum PropertyId {
  CameraReader = "cameraReader",
  Alignment = "alignment",
  AntennaType = "antennaType",
  AndroidAppMode = "androidAppMode",
  NfcCard = "nfcCard",
  Aux = "aux",
  Availability = "availability",
  BatteryFull = "batteryFull",
  BluetoothSupport = "bluetoothSupport",
  Builtin128GbSsd = "builtin128GbSsd",
  Bullet = "bullet",
  CarrierSupport = "carrierSupport",
  CameraAudio = "cameraAudio",
  HubDoor = "hubDoor",
  GreetingMessages = "greetingMessages",
  IdentityAppUnlock = "identityAppUnlock",
  PoePlus2 = "poePlus2",
  Cellular = "cellular",
  CameraResolution = "cameraResolution",
  ClientDevices = "clientDevices",
  Compact = "compact",
  ConnectedDevices = "connectedDevices",
  Connectivity = "connectivity",
  Coverage = "coverage",
  ChargingConnector = "chargingConnector",
  DataPlan = "dataPlan",
  DataProtection = "dataProtection",
  Deployment = "deployment",
  Display = "display",
  Dome = "dome",
  DoorAccessSupport = "doorAccessSupport",
  PinAccess = "pinAccess",
  PaymentTerminalSupport = "paymentTerminalSupport",
  DownlinkSpeed = "downlinkSpeed",
  DriveHotSwap = "driveHotSwap",
  DyingGaspSupport = "dyingGaspSupport",
  DigitalSignageSupport = "digitalSignageSupport",
  EnhancedDetections = "enhancedDetections",
  ErgonomicHandsetWithMuteButton = "ergonomicHandsetWithMuteButton",
  Exit = "exit",
  ExternalAntennaSupport = "externalAntennaSupport",
  FieldOfView = "fieldOfView",
  FormFactor = "formFactor",
  Frequency = "frequency",
  Hand = "hand",
  IdsIpsThroughput = "idsIpsThroughput",
  IntegratedPoeSwitch = "integratedPoeSwitch",
  IntegratedSwitch = "integratedSwitch",
  InteriorDesign = "interiorDesign",
  ImageSensor = "imageSensor",
  LanPorts = "lanPorts",
  LedDisplay = "ledDisplay",
  LicensePlateRecognition = "licensePlateRecognition",
  ManagedCameras = "managedCameras",
  MaximumRange = "maximumRange",
  MaxChargingOutput = "maxChargingOutput",
  Mounting = "mounting",
  NetworkingInterface = "networkingInterface",
  LicenseFreeNextAiSSLInspection = "licenseFreeNextAiSSLInspection",
  NightVision = "nightVision",
  NvrHdd35Bay = "nvrHdd35Bay",
  NvrPowerBackup = "nvrPowerBackup",
  NvrStacking = "nvrStacking",
  OpticalZoom = "opticalZoom",
  OutdoorReady = "outdoorReady",
  PoeAvailability = "poeAvailability",
  PoeOutput = "poeOutput",
  PoePlus = "poePlus",
  PortCount = "portCount",
  PowerMethod = "powerMethod",
  PowerMethodPoe = "powerMethodPoe",
  PowerRedundancy = "powerRedundancy",
  ProAvSupport = "proAvSupport",
  Ptz = "ptz",
  Radio6Ghz = "radio6Ghz",
  Radios = "radios",
  Screen = "screen",
  SimCard = "simCard",
  SmartDetections = "smartDetections",
  Sound = "sound",
  Speakerphone = "speakerphone",
  Storage = "storage",
  StorageCapacity = "storageCapacity",
  StationSecurityModes = "stationSecurityModes",
  SubscriptionLockedAndUnlockedModels = "subscriptionLockedAndUnlockedModels",
  SwitchTechnology = "switchTechnology",
  GpsLocationTracking = "gpsLocationTracking",
  Throughput = "throughput",
  Touchscreen13inch = "touchscreen13inch",
  UnifiAppSuite = "unifiAppSuite",
  UnifiDevices = "unifiDevices",
  UnifiPowerBackup = "unifiPowerBackup",
  Unlock = "unlock",
  Uplink = "uplink",
  UplinkSpeed = "uplinkSpeed",
  TamperResistance = "tamperResistance",
  WanPorts = "wanPorts",
  Waterproof = "waterproof",
  WifiIntegrated = "wifiIntegrated",
  WifiPowerMethod = "wifiPowerMethod",
  Wifi6Technology = "wifi6Technology",
  WifiTechnology = "wifiTechnology",
  Etherlighting = "etherlighting",
  IntegratedWifi6 = "integratedWiFi6",
  AvailablePorts = "availablePorts",
  Ports = "ports",
  IncludedHub = "includedHub",
  IncludedReaders = "includedReaders",
  IncludedAccessories = "includedAccessories",
  Installation = "installation",
  OutputRelays = "outputRelays",
  InputTerminals = "inputTerminals",
  PoePorts = "poePorts",
  AccessPowerMethod = "accessPowerMethod",
  Intercom = "intercom",
  MobileTap = "mobileTap",
  MobileAppAccess = "mobileAppAccess",
  ReaderPowerMethod = "readerPowerMethod",
  BackupRadio = "backupRadio",
  ClientCapacity = "clientCapacity",
  Dimensions = "dimensions",
  IntegratedBluetooth = "integratedBluetooth",
  IntegratedGps = "integratedGps",
  MaximumLinkRange = "maximumLinkRange",
  MaxThroughput = "maxThroughput",
  MaxTotalThroughput = "maxTotalThroughput",
  PrecisionAlignmentMount = "precisionAlignmentMount",
  Weight = "weight",
  MaxLinkRange = "maxLinkRange",
  Bridge = "bridge",
  PoeSwitch = "poeSwitch",
  Technology = "technology",
  NatThroughput = "natThroughput",
  TrafficShaping = "trafficShaping",
  Firewall = "firewall",
  Redundancy = "redundancy",
  MountType = "mountType",
  UispBox = "uispBox",
  LayerTwoFunctionality = "layerTwoFunctionality",
  InternalSwitch = "internalSwitch",
  PoeInput = "poeInput",
  DcPowerOutput = "dcPowerOutput",
  PowerTransportOutput = "powerTransportOutput",
  UpsFunctionality = "upsFunctionality",
  InternalBatterySupport = "internalBatterySupport",
  ExternalBatterySupport = "externalBatterySupport",
  PowerLoadBalancing = "powerLoadBalancing",
  HealthMonitoring = "healthMonitoring",
  SmsLte = "smsLte",
  Touchscreen = "touchscreen",
  WindLoading = "windLoading",
  Swivel = "swivel",
  SwivelPowerMethod = "swivelPowerMethod",
  SwivelSpectralEfficiency = "swivelSpectralEfficiency",
  SwivelPackets = "swivelPackets",
  SwivelLatency = "swivelLatency",
  Denoise = "denoise",
  Focus = "focus",
  DedicatedWiFi = "dedicatedWiFi",
  LinkRange = "linkRange",
  NetworkInterface = "networkInterface",
  SpectralEfficiency = "spectralEfficiency",
  ElevationBeamwidth = "elevationBeamwidth",
  RadiusCoverage = "radiusCoverage",
  AntennaGain = "antennaGain",
  Customization = "customization",
  DedicatedSpectralScanningEngine = "dedicatedSpectralScanningEngine",
  WiFi = "wifi",
  WiFiMounting = "wifiMounting",
  DigitalSignageMode = "digitalSignageMode",
  VideoContentSupport = "videoContentSupport",
  WebMode = "webMode",
  YoutubeMode = "youtubeMode",
  HdmiCec = "hdmiCec",
  UsbCPeripheralSupport = "usbCPeripheralSupport",
  VantagePoint = "vantagePoint",
  LinkPuncturing = "linkPuncturing",
  RfFilteringTechnology = "rfFilteringTechnology",
  Weatherproofing = "weatherproofing",
  RedundantRj45Port = "redundantRj45Port",
  FipsCompliance = "fipsCompliance",
  ExtendedRangeMode6Ghz = "extendedRangeMode6Ghz",
  Directory = "directory",
  TwoWayIntercom = "twoWayIntercom",
  WirelessHeadsetSupport = "wirelessHeadsetSupport",
  WirelessHandsetSupport = "wirelessHandsetSupport",
  PbxSupport = "pbxSupport",
  CrestonNaxNvxCertified = "crestonNaxNvxCertified",
  HighPerformanceSpeaker = "highPerformanceSpeaker",
  PhoneConnectivity = "phoneConnectivity",
  PhoneMounting = "phoneMounting",
  IntegratedWiFi = "integratedWiFi",
  AiRecognition = "aiRecognition",
  PortLayout = "portLayout",
  SwitchingCapacity = "switchingCapacity",
  TotalNonBlockingThroughput = "totalNonBlockingThroughput",
  ForwardingRate = "forwardingRate",
  MgmtPort = "mgmtPort",
  TouchPassSupport = "touchPassSupport",
  AppleTouchPassAccess = "appleTouchPassAccess",
  BuiltInFloodlight = "builtInFloodlight",
  ManagedUnifiDevices = "managedUnifiDevices",
  SimultaneousUsersConnected = "simultaneousUsersConnected",
  MaxWanPortCount = "maxWanPortCount",
  GeneralRedundancy = "generalRedundancy",
  FaceRecognition = "faceRecognition",
  McLagSupport = "mcLagSupport",
  UnasPowerMethod = "unasPowerMethod",
  Capacity = "capacity",
  Power = "power",
  HotSwappableBattery = "hotSwappableBattery",
  FieldReplaceableBattery = "fieldReplaceableBattery",
  NutSupport = "nutSupport",
}

export enum ProductGroupId {
  Bullet = "bullet",
  Compact = "compact",
  DomeTurret = "domeTurret",
  InteriorDesign = "interiorDesign",
  PTZ = "ptz",
  TwoPointFiveTenGigabitEthernet = "twoPointFiveTenGigabitEthernet",
  Aggregation = "aggregation",
  SwitchingCompact = "switchingCompact",
  GigabitEthernet = "gigabitEthernet",
  Enterprise = "enterprise",
  ProfessionalMax = "professionalMax",
  Professional = "professional",
  ProductListDomeTurret = "productListDomeTurret",
  ProductListCompact = "productListCompact",
  ProductListInteriorDesign = "productListInteriorDesign",
  ProductListPTZ = "productListPTZ",
  WiFiFlagShip = "wifiFlagship",
  WiFiWall = "wifiWall",
  WiFiOutdoor = "wifiOutdoor",
  WiFiMegaCapacity = "wifiMegaCapacity",
  WiFiBuildingBridge = "wifiBuildingBridge",
  WiFiE7 = "wifiE7",
}

export enum ProductListGroupId {
  Enterprise = "enterprise",
  Compact = "compact",
  WiFiIntegrated = "wifiIntegrated",
  LargeScale = "largeScale",
  Standard = "standard",
  Utility = "utility",
  Bullet = "bullet",
  DomeTurret = "domeTurret",
  InteriorDesign = "interiorDesign",
  Aggregation = "aggregation",
  Professional = "professional",
  ProfessionalMax = "professionalMax",
  PTZ = "ptz",
  BuildingBridge = "buildingBridge",
  WiFiE7 = "wifiE7",
  Flagship = "flagship",
  MegaCapacity = "megaCapacity",
  Outdoor = "outdoor",
  Wall = "wall",
  DeviceBridge = "deviceBridge",
  ProductListSwitchingEnterprise = "productListSwitchingEnterprise",
  ProductListSwitchingProfessional = "productListSwitchingProfessional",
  ProductListSwitchingValue = "productListSwitchingValue",
  ProductListSwitchingCompact = "productListSwitchingCompact",
  StarterKits = "starterKits",
  ControlHubs = "controlHubs",
  ReadersAndIntercom = "readersAndIntercom",
}

/**
 * Enum retained for internal logic (optional to use in product files)
 */
export enum MutatedPropertyType {
  Translation = "translation",
  String = "string",
  Boolean = "boolean",
  Custom = "custom",
}

/**
 * Property value types using string literals, so product files don’t need to import the enum
 */
type Value<T> = ((storeId: StoreId) => T) | T;

type TranslationValue = {
  type: "translation";
  value: Value<TranslationKey>;
};

type StringValue = {
  type: "string";
  value: Value<string>;
};

type BooleanValue = {
  type: "boolean";
  value: Value<boolean>;
};

type CustomValue = {
  type: "custom";
  value: Value<ReactNode>;
};

/**
 * Union of all supported property value types
 */
export type PropertyValue = {
  /**
   * HACK: make usage of id's a bit more convenient in the data/products/* files
   * by not requiring import of the PropertyId enum
   */
  propertyId: `${PropertyId}`;
  value: (TranslationValue | StringValue | BooleanValue | CustomValue) & {
    // Optional number of asterisks to show ("*")
    asterisk?: number;
  };
};

/**
 * Variant of PropertyValue excluding translation type
 */
export type MutatedPropertyValue = Omit<PropertyValue, "value"> & {
  value: Exclude<PropertyValue["value"], TranslationValue>;
};

type Badge = {
  variant?: `${BadgeVariants}`;
  value: TranslationValue | StringValue;
};

type StorePathParts = {
  productSlug: string;
  // TODO: Preferably this would be ChildCategorySlug
  // TODO: Remove the string to check if categories are in order
  category: `${CategorySlug}`;
  collectionSlug?: string;
  variant?: string;
};

// @NOTE we only have unifi on ui.com
type ProductLine = "unifi";

export type Product = {
  /**
   * HACK: make usage of id's a bit more convenient in the data/products/* files as you don't need import ProductId
   */
  id: `${ProductId}` | EmptyColumnId;
  // title: TranslationKey;
  productTitle: string;
  /**
   * Makes product title clickable and directs user to techspecs
   */
  techspecsPath?: [
    siteId: SiteId.TechSpecs,
    pathname:
      | `/${ProductLine}`
      | `/${ProductLine}/${PrimaryCategoryId}`
      | `/${ProductLine}/${PrimaryCategoryId}/${string}`,
    searchParams?: SiteUrl[2],
    fragment?: SiteUrl[3],
  ];
  /**
   * Renders "Start Trial" button
   */
  startTrialLink?: string;
  /**
   * Renders "Buy Now" button
   */
  storePathParts?:
    | StorePathParts
    | ((storeId: StoreId) => StorePathParts | undefined);
  /**
   * By sku (name) we can find product in the store
   */
  sku?: string;
  /**
   * Renders "Learn More" button
   */
  learnMoreLink?: SiteUrl;

  description?: TranslationKey;
  descriptionMaxWidth?: number;
  /**
   * Adds button-like "Coming Soon" badge the same place where Buy Now button is
   *
   * Can be list of store IDs to show badge only for specific stores or `true` to show for all stores
   */
  isComingSoon?: StoreId[] | boolean;
  comingSoonButtonText?:
    | TranslationKey
    | ((storeId: StoreId) => TranslationKey | undefined);
  /** Add "New" badge */
  isNew?: boolean;
  /**
   * Adds custom badges
   */
  customBadges?: Badge[];
  image: { src: StaticImageData | string; width: number; height: number };
  propertyValues: PropertyValue[];
  productListGroup?: ProductListGroup;
  /**
   * Added after data rows
   */
  notes?: TranslationKey[];
  /**
   * Footnotes
   */
  footnotes?: (
    | TranslationKey
    | ((storeId: StoreId) => TranslationKey | undefined)
  )[];
  productNote?: TranslationKey;
};

export type MutatedProduct = Omit<Product, "propertyValues" | "footnotes"> & {
  propertyValues: MutatedPropertyValue[];
  addToCartButton?: ReactNode;
  footnotes?: TranslationKey[];
};

export type Property = {
  /**
   * HACK: make usage of id's a bit more convenient in the data/products/* files as you don't need import ProductId
   */
  id: `${PropertyId}`;
  title: TranslationKey;
  icon: string | StaticImageData;
  tooltip?: TranslationKey;
  tooltipWidth?: number;
};

export type MutatedProperty = Omit<Property, "title" | "tooltip"> & {
  title: string;
  tooltip?: string;
};

export type ProductListGroup = {
  id: `${ProductListGroupId}`;
  title: TranslationKey;
  icon: { src: string | StaticImageData; width: number; height: number };
  /** For Buttons that should exist outside of the button container
   * EX: Enterprise 7 in the WiFi Overview compare
   */
  isSpotlightGroup?: boolean;
};

export type Section = {
  // TODO: do I still need `id` now that we don't have TogglableStaticCompareTable?
  id?: string;
  products: Product[];
  properties: Property[];
  columnWidth: number;
  propertyColumnWidth?: number;
  showProductGroups?: boolean;
  /**
   * Determine if badges should be shown
   * that are added to products
   */
  showBadges?: boolean;
  isOverviewPage?: boolean;
  isStaticTable?: boolean;
};

export type SelectedProduct = { productId: ProductId };
